import { ActionIcon, Tooltip } from "@mantine/core";

import { IconRefresh } from "@tabler/icons-react";

import { toast } from "@/utils/notifications";

export type SectionHeaderRefreshProps = {
  /** Accessible name, e.g. "Contact Information" or "Statements" */
  sectionLabel: string;
  isRefreshing: boolean;
  onRefresh: () => void | Promise<void>;
  /**
   * Tooltip text. Defaults to "Refresh this section", which is what every existing header shows.
   * Pass a specific label where naming the section helps, e.g. "Refresh Results".
   */
  tooltipLabel?: string;
  /** Show an arrow pointing at the icon. Off by default, so existing headers stay as they are. */
  withTooltipArrow?: boolean;
};

/**
 * Compact refresh control for section headers (next to the section title). Shared across modules —
 * profile cards, billing tabs — so every section refresh looks and behaves the same.
 */
export function SectionHeaderRefresh({
  sectionLabel,
  isRefreshing,
  onRefresh,
  tooltipLabel = "Refresh this section",
  withTooltipArrow = false,
}: SectionHeaderRefreshProps) {
  const handleClick = () => {
    Promise.resolve(onRefresh()).catch((error) => {
      console.error(`Failed to refresh ${sectionLabel}:`, error);
      toast({
        title: "Refresh failed",
        message: `Unable to refresh ${sectionLabel}. Please try again.`,
        type: "error",
      });
    });
  };

  return (
    <Tooltip label={tooltipLabel} withArrow={withTooltipArrow}>
      <ActionIcon
        color="gray.6"
        variant="subtle"
        onClick={handleClick}
        loading={isRefreshing}
        disabled={isRefreshing}
        aria-label={`Refresh ${sectionLabel}`}
      >
        <IconRefresh size={18} />
      </ActionIcon>
    </Tooltip>
  );
}
